import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ErrorsContext } from './ErrorsContext';
import ModalPaidSubscriptionRequired from 'Components/Molecules/ModalPaidSubscriptionRequired/ModalPaidSubscriptionRequired';
import imageWrongPass from 'assets/icon-wrong-pass.svg';
import * as errorMonitoring from 'Utils/errorMonitoring';
import PlansAndPricingProvider from 'Components/Organisms/PlansAndPricingProvider';
import Dialog from 'Components/Molecules/Dialog/Dialog';
import { Trans, useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import Maintenance from 'Pages/Maintenance';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'Utils/constants';

export default function ErrorsProvider({ children }) {
    const [showUnknownError, setShowUnknownError] = useState(false);
    const [showPaidSubscriptionRequired, setShowPaidSubscriptionRequired] = useState(false);
    const [showPlansAndPricing, setShowPlansAndPricing] = useState(false);
    const [showMaintenance, setShowMaintenance] = useState(false);
    const [connectorBroken, setConnectorBroken] = useState(null);

    const { t } = useTranslation('common');
    const navigate = useNavigate();

    const showUnknownErrorHandler = useCallback((error, extraData = {}) => {
        if (error?.response?.status === 401) {
            errorMonitoring.trackError(error, extraData);
            return;
        }

        const hasNoWorkspacesError =
            error?.response?.status === 400 && error?.response?.data?.description === 'no_workspaces_available';

        if (hasNoWorkspacesError) {
            navigate(ROUTES.selectWorkspace);
            return;
        }

        const hasAxiosRequiresPaidError =
            error?.response?.status === 400 && error?.response?.data?.description === 'requires_paid_subscription';

        if (hasAxiosRequiresPaidError) {
            setShowPaidSubscriptionRequired(true);
            return;
        }

        const hasConnectorBrokenError =
            error?.response?.status === 400 && error?.response?.data?.description === 'connector_broken';

        if (hasConnectorBrokenError) {
            setConnectorBroken({ message: error.response.data.info?.health_status_message ?? null });
            return;
        }

        const hasAxiosMaintenanceError =
            error?.response?.status === 503 && error?.response?.data?.description === 'in_maintenance';

        if (hasAxiosMaintenanceError) {
            setShowMaintenance(true);
            return;
        }

        if (error) {
            errorMonitoring.trackError(error, extraData);
        }

        setShowUnknownError(true);
    }, []);

    const contextValue = useMemo(
        () => ({
            showUnknownError: showUnknownErrorHandler,
        }),
        [showUnknownErrorHandler],
    );

    return (
        <ErrorsContext.Provider value={contextValue}>
            <PlansAndPricingProvider open={showPlansAndPricing} setOpen={setShowPlansAndPricing}>
                {showMaintenance && <Maintenance />}

                <ModalPaidSubscriptionRequired
                    open={showPaidSubscriptionRequired}
                    onClose={() => setShowPaidSubscriptionRequired(false)}
                    showPlansAndPricing={() => setShowPlansAndPricing(true)}
                />

                <Dialog open={showUnknownError} onClose={() => setShowUnknownError(false)} size="sm">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <img src={imageWrongPass} alt="" />
                        </div>
                        <h3 className="font-medium mt-6">{t('unknown-error')}</h3>
                        <div className="mt-2 mb-12 text-gray-500">
                            <Trans t={t} i18nKey="unknown-error-description" />
                        </div>
                        <Button variant="primary" onClick={() => setShowUnknownError(false)}>
                            {t('close')}
                        </Button>
                    </div>
                </Dialog>

                <Dialog open={!!connectorBroken} onClose={() => setConnectorBroken(null)} size="sm">
                    <div className="text-center">
                        <h3 className="font-medium">{t('provider-connections.bank-outage-error-title')}</h3>
                        <div className="mt-2 mb-12 text-gray-500">
                            {connectorBroken?.message || t('provider-connections.bank-outage-error-description')}
                        </div>
                        <Button variant="primary" onClick={() => setConnectorBroken(null)}>
                            {t('close')}
                        </Button>
                    </div>
                </Dialog>

                {children}
            </PlansAndPricingProvider>
        </ErrorsContext.Provider>
    );
}

ErrorsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
